import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  Autocomplete,
} from "@mui/material";
import axios from "../../api";
import Locations from "examples/ReuseFunctions/Locations";
import SoftTypography from "components/SoftTypography";
import * as Yup from "yup";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Region is required"),
  address_type: Yup.string().required("Address type is required"),
  address: Yup.string().required("Address is required"),
});
interface Address {
  state_id: number;
  city_id: number;
  region_id: number;
  address_id: number;
  customer_id: number;
  is_primary: boolean;
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
}
interface EditAddressProps {
  data: Address | null;
  fetchSuccess: any;
  fetchError: any;
  fetchAddressData: () => Promise<void>;
  onClose: () => void;
}
const EditAddress = ({
  data,
  fetchSuccess,
  fetchError,
  fetchAddressData,
  onClose,
}: EditAddressProps) => {
  const [initialState, setInitialState] = useState<{
    state_id: number;
    state_name: string;
  } | null>({
    state_id: data?.state_id ?? 0,
    state_name: data?.state_name ?? "",
  });
  const [initialCity, setInitialCity] = useState<{
    city_id: number;
    city_name: string;
  } | null>({
    city_id: data?.city_id ?? 0,
    city_name: data?.city_name ?? "",
  });
  const [initialRegion, setInitialRegion] = useState<{
    region_id: number;
    region_name: string;
  } | null>({
    region_id: data?.region_id ?? 0,
    region_name: data?.region_name ?? "",
  });

  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  useEffect(() => {
    if (data) {
      setSelectedState(data.state_id);
      setSelectedCity(data.city_id);
    }
  }, [data, setSelectedState, setSelectedCity]);
  return (
    <Formik
      initialValues={{
        state_id: data?.state_id || "",
        city_id: data?.city_id || "",
        region_id: data?.region_id || "",
        address_type: data?.address_type || "",
        address: data?.address || "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        try {
          const response = await axios.put(
            `/address/${data?.address_id}`,
            values
          );
          fetchSuccess(response.data.message);
          fetchAddressData();
          onClose();
        } catch (error) {
          if (error instanceof AxiosError && error.response) {
            fetchError(error.response.data.error);
          }
        }
      }}
    >
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Form className="update-address-popup">
          <Box
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 18px 35px rgba(15,23,42,0.12)",
              p: { xs: 2, sm: 3 },
            }}
          >
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  State
                </SoftTypography>
                <Autocomplete
                  id="state-autocomplete"
                  options={states ?? []}
                  getOptionLabel={(option) => option.state_name || ""}
                  value={values.state_id ? initialState : null}
                  onChange={(event, newValue) => {
                    setFieldValue("city_id", "");
                    setFieldValue("region_id", "");
                    setFieldValue(
                      "state_id",
                      newValue ? newValue.state_id : ""
                    );
                    setInitialState(newValue ?? null);
                    setSelectedState(newValue ? newValue.state_id : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="state_id"
                      variant="outlined"
                      error={!!errors.state_id && touched.state_id}
                      helperText={touched.state_id && errors.state_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  City
                </SoftTypography>
                <Autocomplete
                  id="city-autocomplete"
                  options={cities ?? []}
                  getOptionLabel={(option) => option.city_name || ""}
                  value={values.city_id ? initialCity : null}
                  onChange={(event, newValue) => {
                    setFieldValue("region_id", "");
                    setFieldValue("city_id", newValue ? newValue.city_id : "");
                    setInitialCity(newValue ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="city_id"
                      variant="outlined"
                      error={!!errors.city_id && touched.city_id}
                      helperText={touched.city_id && errors.city_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold">
                  Area
                </SoftTypography>
                <Autocomplete
                  id="region-autocomplete"
                  options={areas ?? []}
                  getOptionLabel={(option) => option.region_name || ""}
                  value={values.region_id ? initialRegion : null}
                  onChange={(event, newValue) => {
                    setFieldValue(
                      "region_id",
                      newValue ? newValue.region_id : ""
                    );
                    setInitialRegion(newValue ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="region_id"
                      variant="outlined"
                      error={!!errors.region_id && touched.region_id}
                      helperText={touched.region_id && errors.region_id}
                      fullWidth
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <SoftTypography variant="caption" fontWeight="bold">
                Address Type
              </SoftTypography>
              <TextField
                fullWidth
                placeholder="Address Type"
                name="address_type"
                value={values.address_type}
                onChange={handleChange}
                error={!!errors.address_type && touched.address_type}
                helperText={touched.address_type && errors.address_type}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftTypography variant="caption" fontWeight="bold">
                Address
              </SoftTypography>
              <TextField
                fullWidth
                placeholder="Address"
                name="address"
                value={values.address}
                onChange={handleChange}
                error={!!errors.address && touched.address}
                helperText={touched.address && errors.address}
              />
            </Grid>
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="success-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                    }}
                    variant="contained"
                    type="submit"
                    color="success"
                  >
                    Update
                  </Button>

                  <Button
                    className="cancel-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                      ml: 2.5,
                    }}
                    variant="contained"
                    color="warning"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default EditAddress;
